import { isValidLocalDate } from '@shared/calendar';
import { useEffect, useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useChangeStartDate } from '@/hooks/use-users';
import { formatLocalDate } from '@/lib/format';
import type { AdminUser } from '@/types/users';

interface StartDateDialogProps {
  user: AdminUser;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function StartDateDialog({ user, open, onOpenChange }: StartDateDialogProps): JSX.Element {
  const [value, setValue] = useState(user.startDate);
  const changeStartDate = useChangeStartDate();

  useEffect(() => {
    if (open) setValue(user.startDate);
  }, [open, user.startDate]);

  function onSubmit(event: FormEvent): void {
    event.preventDefault();
    if (!isValidLocalDate(value)) return;
    changeStartDate.mutate(
      { id: user.id, startDate: value },
      {
        onSuccess: () => {
          toast.success('Start date updated.');
          onOpenChange(false);
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={onSubmit} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>Edit start date</DialogTitle>
            <DialogDescription>
              {user.name}&apos;s current start date is <span className="font-mono tabular-nums">{formatLocalDate(user.startDate)}</span>.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-2">
            <Label htmlFor={`start-date-${user.id}`}>Start date</Label>
            <Input id={`start-date-${user.id}`} type="date" value={value} onChange={(e) => setValue(e.target.value)} required />
          </div>
          <DialogFooter>
            <Button type="submit" className="flex-1" disabled={!isValidLocalDate(value) || changeStartDate.isPending}>
              Save
            </Button>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
